/* v3.18-1289 — Ghost AI Call Dialog
 *
 * First implementation of the reusable <ClientLocalTime> component.
 * When Ghost's "Call Client" action is invoked, this dialog appears
 * with the client's local time + color-coded quiet-hours status.
 *
 * Per operator directive:
 *   • Call Now is NEVER disabled.
 *   • The dialog adopts a red warning theme after 9pm local, while
 *     remaining fully readable.
 *   • The status theming is driven by the reusable component; this
 *     dialog contains ZERO timezone logic of its own.
 */
import { useState } from 'react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Phone, X } from 'lucide-react';
import ClientLocalTime from './ClientLocalTime';

export default function GhostAICallDialog({
  open,
  onOpenChange,
  clientId,
  clientName,
  onCallNow,
}) {
  const [status, setStatus] = useState('green');
  const themeRing =
    status === 'red'    ? 'ring-2 ring-red-300' :
    status === 'yellow' ? 'ring-1 ring-amber-300' :
    '';
  const themeBg =
    status === 'red' ? 'bg-red-50' : 'bg-white';

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-md ${themeBg} ${themeRing}`}
        data-testid="ghost-ai-call-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone size={18} />
            <span data-testid="ghost-ai-call-dialog-title">Call {clientName || 'client'}</span>
          </DialogTitle>
          <DialogDescription>
            Confirm you&apos;re comfortable calling — the client&apos;s local time
            drives the quiet-hours warning below.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <ClientLocalTime
            clientId={clientId}
            variant="banner"
            onStatus={(s) => setStatus(s.status)}
          />
        </div>

        <DialogFooter className="flex-row justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange?.(false)} data-testid="ghost-ai-call-dialog-cancel">
            <X size={14} className="mr-1" /> Cancel
          </Button>
          <Button
            variant={status === 'red' ? 'destructive' : 'default'}
            onClick={() => { onCallNow?.(); onOpenChange?.(false); }}
            data-testid="ghost-ai-call-dialog-call-now"
          >
            <Phone size={14} className="mr-1" /> Call Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
